import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../hooks/useApi';
import { api, authApi } from '../lib/api';
import { Store, TenantContext } from './TenantContext';
import { resolveTenantCapabilities, type BooleanCapabilityKey } from '../lib/capabilities';

const STORE_KEY = 'metadesk_current_store_id';
const ALL_STORES_KEY = 'maddox_all_stores_view';

const readFlag = (key: string) => {
    try {
        return localStorage.getItem(key) === '1';
    } catch {
        return false;
    }
};
const writeStorage = (key: string, value: string | null) => {
    try {
        if (value === null) localStorage.removeItem(key);
        else localStorage.setItem(key, value);
    } catch {
        /* private mode: the choice just isn't remembered */
    }
};

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const queryClient = useQueryClient();
    const [currentStore, setCurrentStoreState] = useState<Store | undefined>(undefined);
    const [allStoresChosen, setAllStoresChosen] = useState<boolean>(() => readFlag(ALL_STORES_KEY));
    const cachedUser = api.getCurrentUser();
    const { data: queriedUser, isLoading: isUserLoading } = useCurrentUser();
    const user = queriedUser ?? cachedUser ?? null;
    const isLoading = !!api.getToken() && !!api.getTenantId() && !user && isUserLoading;

    useEffect(() => {
        if (queriedUser) {
            api.setCurrentUser(queriedUser);
        }
    }, [queriedUser]);

    const tenant = user?.tenant;

    const stores = React.useMemo(() => {
        const allStores = tenant?.stores ?? [];
        if (!allStores.length) return allStores;

        const activeStores = allStores.filter((store) => store.isActive !== false);
        const storesWithOperationalWarehouses = activeStores.filter((store) => {
            if (!Array.isArray(store.warehouses) || store.warehouses.length === 0) {
                return true;
            }

            return store.warehouses.some((warehouse) => warehouse.isActive !== false);
        });

        const candidateStores =
            storesWithOperationalWarehouses.length > 0
                ? storesWithOperationalWarehouses
                : activeStores;

        if (!user) return candidateStores;

        // Backend scope is the authority: a cashier sees only the store they
        // sell from; a manager only their stores.
        const scope = user.locationScope;
        if (scope && !scope.isGlobal) {
            const allowed = new Set(scope.allowedStoreIds);
            return candidateStores.filter((store) => allowed.has(store.id));
        }

        const allowedStoreIds = new Set(
            (user.allowedWarehouses || [])
                .map((warehouse) => warehouse.storeId)
                .filter(Boolean)
        );

        if (allowedStoreIds.size > 0 && !scope?.isGlobal) {
            return candidateStores.filter((store) => allowedStoreIds.has(store.id));
        }

        if (user.role === 'OWNER' || user.isSuperAdmin) return candidateStores;
        return candidateStores;
    }, [tenant?.stores, user]);

    useEffect(() => {
        if (stores.length > 0) {
            let savedStoreId: string | null = null;
            try {
                savedStoreId = localStorage.getItem(STORE_KEY);
            } catch {
                savedStoreId = null;
            }
            const savedStore = stores.find((store) => store.id === savedStoreId);
            const bound = user?.locationScope?.boundStoreId;
            const boundStore = bound ? stores.find((store) => store.id === bound) : undefined;

            if (boundStore) {
                setCurrentStoreState(boundStore);
                writeStorage(STORE_KEY, boundStore.id);
            } else if (savedStore) {
                setCurrentStoreState(savedStore);
            } else {
                setCurrentStoreState(stores[0]);
                writeStorage(STORE_KEY, stores[0].id);
            }
        }
    }, [stores, user?.locationScope?.boundStoreId]);

    const locationScope = user?.locationScope ?? null;
    const tenantStoreCount = (tenant?.stores ?? []).filter((store) => store.isActive !== false).length;
    const canViewAllStores =
        stores.length > 1 &&
        (locationScope ? locationScope.isGlobal : user?.role === 'OWNER' || !!user?.isSuperAdmin);
    const isAllStores = canViewAllStores && allStoresChosen;

    const setCurrentStore = (store: Store) => {
        setCurrentStoreState(store);
        setAllStoresChosen(false);
        writeStorage(STORE_KEY, store.id);
        writeStorage(ALL_STORES_KEY, null);
    };

    const setAllStores = () => {
        if (!canViewAllStores) return;
        setAllStoresChosen(true);
        writeStorage(ALL_STORES_KEY, '1');
    };

    const refreshUser = async () => {
        try {
            const freshUser = await authApi.me();
            api.setCurrentUser(freshUser);
            queryClient.setQueryData(['me', api.getTenantId()], freshUser);
        } catch (err) {
            console.error('[TenantContext] refreshUser failed', err);
        }
    };

    // Effective capabilities: backend payload when present, else a local
    // fallback mirroring the backend resolver (older backend / cached user).
    const capabilities = React.useMemo(
        () => resolveTenantCapabilities(tenant),
        [tenant],
    );

    const hasCapability = (key: BooleanCapabilityKey): boolean => capabilities[key] === true;

    const hasFeature = (featureKey: string): boolean => {
        return !!tenant?.features?.[featureKey];
    };

    const isVertical = (vertical: string): boolean => {
        return tenant?.industry === vertical;
    };

    const value = {
        tenant,
        tenantId: tenant?.id,
        tenantName: tenant?.name,
        vertical: tenant?.industry,
        features: tenant?.features || {},
        capabilities,
        hasCapability,
        stores,
        currentStore,
        setCurrentStore,
        tenantStoreCount,
        locationScope,
        canViewAllStores,
        isAllStores,
        setAllStores,
        refreshUser,
        hasFeature,
        isVertical,
        isLoading,
    };

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
};
